import type { AdSlotKey } from "@gooner/shared";

/**
 * `ad_slot_rendered` — what one AdSlot mount ended up showing, reported ONCE.
 *
 * The pure half of the event, pulled out of AdSlot so tools/checks/ad-slot-rendered.mts
 * can run it: where the slot sits, how its outcome maps onto the !146 house-card logic
 * (lib/adHouseCard), and the fire-once latch that keeps StrictMode and an early unmount
 * from producing a second event or a stray one.
 *
 * Only a LIVE slot ever reaches this: ads off, no zone, safe mode (the grey box) and
 * members (the wrapper never mounts the inner slot) never push a zone, so they never
 * resolve and never report.
 */

/** Where the slot is on the page. Derived from the slot key — every call site passes one
 *  key and each key lives in exactly one place (Browse in-feed / sidebar, SiteFooter,
 *  the /views rail on Home), so no extra prop is needed. */
export type AdPlacement = "in-feed" | "footer" | "sidebar" | "rail" | "collections" | "feed" | "rooms" | "collection";
export const AD_PLACEMENT: Record<AdSlotKey, AdPlacement> = {
  browseInFeed: "in-feed",
  footer: "footer",
  browseSidebar: "sidebar",
  viewsRail: "rail",
  collectionsInFeed: "collections",
  feedInFeed: "feed",
  roomsList: "rooms",
  collectionPage: "collection",
};

/**
 * One outcome per mount, decided in this order:
 *
 *  1. an iframe arrived                          -> filled (never swapped afterwards)
 *  2. the house card is on screen                -> house_card, reason blocked | timeout
 *  3. the script failed / was blocked, no card   -> blocked
 *  4. the FILL_TIMEOUT passed, no iframe, no card -> no_iframe
 *  5. none of the above                          -> null (still waiting, report nothing)
 *
 * With the !146 logic every live slot that does not fill shows the house card, so today
 * an unfilled slot reports house_card + reason; `blocked` and `no_iframe` are what the
 * same mount reports if a future change stops showing the card for that case.
 */
export type AdRenderedOutcome = "filled" | "no_iframe" | "blocked" | "house_card";
export type AdHouseReason = "blocked" | "timeout";
export interface AdResolution {
  outcome: AdRenderedOutcome;
  reason?: AdHouseReason;
}
export function adRenderedOutcome(e: {
  iframe: boolean;
  scriptFailed: boolean;
  deadlinePassed: boolean;
  houseCard: boolean;
}): AdResolution | null {
  if (e.iframe) return { outcome: "filled" };
  const reason: AdHouseReason | null = e.scriptFailed ? "blocked" : e.deadlinePassed ? "timeout" : null;
  if (!reason) return null;
  if (e.houseCard) return { outcome: "house_card", reason };
  return { outcome: reason === "blocked" ? "blocked" : "no_iframe" };
}

/**
 * The event's properties, CLOSED here (2026-10-21, for analytics-scrub).
 *
 * The central scrub in analytics.ts only rewrites strings — credential params and id
 * paths — and keeps every key it is handed, so an extra field or an email would ride
 * straight through it. This builder is therefore the allowlist, the way lib/gold.ts and
 * lib/partners.ts close their events: exactly zone, placement, outcome, reason (house_card
 * only) and viewport_width are copied out, each checked against its type or enum. Anything
 * that does not fit returns null and AdSlot sends nothing. `zone` must be digits — the
 * same rule insMarkup applies — so a settings file edited by hand cannot put a url, an
 * email or any other string into the event.
 */
// A type alias, not an interface, so it is assignable to track()'s Record<string, unknown>.
export type AdSlotRenderedProps = {
  zone: string;
  placement: AdPlacement;
  outcome: AdRenderedOutcome;
  reason?: AdHouseReason;
  viewport_width: number;
};
export const AD_SLOT_RENDERED_KEYS = ["zone", "placement", "outcome", "reason", "viewport_width"] as const;
const ZONE_ID = /^[0-9]{1,16}$/;
const OUTCOMES: readonly string[] = ["filled", "no_iframe", "blocked", "house_card"];
const REASONS: readonly string[] = ["blocked", "timeout"];
export function adSlotRenderedProps(
  slot: AdSlotKey,
  zone: string,
  r: AdResolution,
  viewportWidth: number,
): AdSlotRenderedProps | null {
  const placement = Object.prototype.hasOwnProperty.call(AD_PLACEMENT, slot) ? AD_PLACEMENT[slot] : undefined;
  const outcome = r?.outcome;
  const reason = outcome === "house_card" ? r.reason : undefined;
  if (!placement || typeof zone !== "string" || !ZONE_ID.test(zone)) return null;
  if (!OUTCOMES.includes(outcome)) return null;
  if (outcome === "house_card" && !(typeof reason === "string" && REASONS.includes(reason))) return null;
  if (typeof viewportWidth !== "number" || !Number.isFinite(viewportWidth) || viewportWidth < 0) return null;
  return {
    zone,
    placement,
    outcome,
    ...(reason ? { reason } : {}),
    viewport_width: Math.round(viewportWidth),
  };
}

/**
 * The fire-once latch, one per AdSlot instance (held in a ref).
 *
 *  - `mount`/`unmount` track whether the slot is on the page. StrictMode's dev-only
 *    unmount+remount runs both on the SAME instance, so the latch ends mounted and the
 *    `fired` flag survives — the second effect pass cannot fire again.
 *  - `fire` sends only while mounted and only the first time; afterwards (and after a
 *    real unmount, when stray timers and promise callbacks may still settle) it is a
 *    no-op. Returns whether it sent.
 */
export interface FireOnce {
  mount(): void;
  unmount(): void;
  fire(send: () => void): boolean;
}
export function createFireOnce(): FireOnce {
  let mounted = false;
  let fired = false;
  return {
    mount: () => {
      mounted = true;
    },
    unmount: () => {
      mounted = false;
    },
    fire: (send) => {
      if (!mounted || fired) return false;
      fired = true;
      send();
      return true;
    },
  };
}
